import {createAsyncThunk, createSelector, createSlice} from "@reduxjs/toolkit"
import axios from "axios"

export const searchUsersByLastName = createAsyncThunk(
	"userSearch/searchUsersByLastName",
	async (query, {getState, rejectWithValue}) => {
		const currentState = await getState()
		const userSelection = currentState.userSearch.selectedUsers

		const {data} = await axios
			.get(`/api/user?search=${query}`)
			.catch((error) => {
				console.error("SEARCHING FOR MEMBERS TO SHARE CITATION: \n", error)
				rejectWithValue("Failed while searching. Please try again.")
			})

		if(data) {
			return data.filter(serverUserData => !userSelection.some(user => user.id === serverUserData.id))
		}
	}
)

export const searchNonWorkspaceUsersByLastName = createAsyncThunk(
	"userSearch/searchNonWorkspaceUsersByLastName",
	async (query, {getState, rejectWithValue}) => {
		const currentState = await getState()
		const workspaceId = currentState.workspace.primaryId

		const {data} = await axios
			.get(`/api/workspace/${workspaceId}/user?search=${query}`)
			.catch((error) => {
				console.error("SEARCHING FOR NON-WORKSPACE MEMBERS FAILED: \n", error)
				rejectWithValue("Failed while searching. Please try again.")
			})

		if(data) {
			return data
		}
	}
)

export const userSearchSlice = createSlice({
	name: "userSearch",
	initialState: {
		results: {},
		selectedUsers: [],
		isSearching: false,
		searchError: null
	},
	reducers: {
		resetUserSearch: (state) => {
			state.isSearching = false
			state.results = {}
			state.selectedUsers = []
		},
		clearResults: (state) => {
			state.results = {}
		},
		toggleIsSearchingTrue: (state) => {
			state.isSearching = true
		},
		toggleIsSearchingFalse: (state) => {
			state.isSearching = false
		},
		addUsersToResults: (state, action) => {
			const newResultsObj = {}
			if (action.payload.length) {
				action.payload.forEach((user) => {
					newResultsObj[user.id] = user
				})
			}
			state.results = newResultsObj
			state.isSearching = false
		},
		addUserToSelectedUsers: (state, action) => {
			const selectedUser = action.payload
			if(!state.selectedUsers.some(user => user.id === selectedUser.id)) {
				state.selectedUsers.push(selectedUser)
				delete state.results[selectedUser.id]
			} else {
				state.searchError = "User already selected."
			}
		},
		removeUserFromSelectedUsers: (state, action) => {
			const selectedUsers = state.selectedUsers
			const indexOfUserToRemove = state.selectedUsers.findIndex(user => user.id === action.payload.id)
			const removedUser = selectedUsers.splice(indexOfUserToRemove, 1)[0]

			state.selectedUsers = selectedUsers
			state.results[removedUser.id] = removedUser
		},
		setUserSearchError: (state, action) => {
			state.isSearching = false
			state.searchError = action.payload
		},
		resetUserSearchError: (state) => {
			state.searchError = null
		}
	},
	extraReducers: (builder) => {
		builder.addCase(searchUsersByLastName.pending, userSearchSlice.caseReducers.toggleIsSearchingTrue)
		builder.addCase(searchUsersByLastName.fulfilled, userSearchSlice.caseReducers.addUsersToResults)
		builder.addCase(searchUsersByLastName.rejected, userSearchSlice.caseReducers.setUserSearchError)
		builder.addCase(searchNonWorkspaceUsersByLastName.pending, userSearchSlice.caseReducers.toggleIsSearchingTrue)
		builder.addCase(searchNonWorkspaceUsersByLastName.fulfilled, userSearchSlice.caseReducers.addUsersToResults)
		builder.addCase(searchNonWorkspaceUsersByLastName.rejected, userSearchSlice.caseReducers.setUserSearchError)
	}
})

export const {
	resetUserSearch,
	addUserToSelectedUsers,
	setUserSearchError,
	resetUserSearchError,
	removeUserFromSelectedUsers,
	clearResults,
} = userSearchSlice.actions

const currentResults = (state) => state.userSearch.results
export const selectSearchResults = createSelector(
	[currentResults],
	(results) => Object.values(results)
)
export const selectSelectedUsers = (state) => state.userSearch.selectedUsers
export const selectIsSearchingUsers = (state) => state.userSearch.isSearching
export const selectUserSearchError = (state) => state.userSearch.searchError

export default userSearchSlice.reducer